import { debounce, throttle } from 'lodash-es';

export { debounce, throttle };

export function getVisibleWidth() {
  return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
}

export function getVisibleHeight() {
  return window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
}

export function getElementHeight(element: HTMLElement | null) {
  if (!element) return 0;
  return element.clientHeight || element.offsetHeight || element.scrollHeight;
}

export function getElementWidth(element: HTMLElement | null) {
  if (!element) return 0;
  return element.clientWidth || element.offsetWidth || element.scrollWidth;
}

export const getTemporaryElementDimensions = (innerHTML: string) => {
  const div = document.createElement('div');
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.innerHTML = innerHTML;
  document.body.appendChild(div);
  const rect = div.getBoundingClientRect();
  const { height, width } = rect;
  document.body.removeChild(div);
  return { height, width };
};

export function cookiesAllowed() {
  try {
    document.cookie = 'checkCookie=yes;';
    return document.cookie.indexOf('checkCookie=') > -1;
  } catch (e) {
    return false;
  }
}

function createStorage() {
  const _allowed = cookiesAllowed() && Boolean(localStorage);

  return {
    set(key: string, value: string): boolean {
      if (!_allowed) return false;

      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        return false;
      }
    },
    get(key: string): string | null {
      if (!_allowed) return null;

      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    remove(key: string): boolean {
      if (!_allowed) return false;

      try {
        localStorage.removeItem(key);
        return true;
      } catch (e) {
        return false;
      }
    },

    clear() {
      if (!_allowed) return false;

      try {
        localStorage.clear();
        return true;
      } catch (e) {
        return false;
      }
    },
  };
}
export const storage = createStorage();

export const scrollIntoView = (element: HTMLElement, options?: ScrollIntoViewOptions) => {
  if ('scrollIntoViewIfNeeded' in element && typeof element.scrollIntoViewIfNeeded === 'function') {
    // This is a non-standard API but where it's available, it works much better so we try to use it.
    element.scrollIntoViewIfNeeded(options);
  } else {
    if (!isElementInViewport(element)) {
      element.scrollIntoView(options);
    }
  }
};

export const noop = () => {};

export const blurActiveElement = () => {
  requestAnimationFrame(() => {
    (document.activeElement as HTMLElement)?.blur();
  });
};

export const downloadFile = (file: Blob | MediaSource, filename: string) => {
  const url = window.URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
};

export const getRawTextFromHtml = (html: string) => {
  const node = document.createElement('div');
  node.innerHTML = html;
  return node.innerText;
};

export const clamp = (min: number, value: number, max: number) => {
  return Math.max(Math.min(max, value), min);
};

type DownloadAsCsvProps = {
  content: string;
  filenamePrefix?: string;
};

export const downloadAsCsv = ({ content, filenamePrefix }: DownloadAsCsvProps) => {
  const filename = filenamePrefix ? `${filenamePrefix}-export.csv` : 'export.csv';
  const link = window.document.createElement('a');
  link.setAttribute('href', 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURIComponent(content));
  link.setAttribute('download', filename);
  link.click();
};

export const urlToFile = async ({
  url,
  filename,
  mimeType = 'image/jpeg',
}: {
  url: string;
  filename: string;
  mimeType?: string;
}): Promise<File> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${url}. Status: ${response.status}`);
  }
  const blob = await response.blob();
  const file = new File([blob], filename, { type: mimeType });

  return file;
};

let globalTransitionNode: HTMLStyleElement | null = null;
let globalTransitionStyle: Text | null = null;

if (typeof document !== 'undefined') {
  globalTransitionNode = document.createElement('style');
  globalTransitionStyle = document.createTextNode(`* {
    -webkit-transition: none !important;
    -moz-transition: none !important;
    -o-transition: none !important;
    -ms-transition: none !important;
    transition: none !important;
  }`);
  globalTransitionNode.appendChild(globalTransitionStyle);
}

export const createPersistentMeasurement = (staticStructureHtml: string) => {
  if (typeof document === 'undefined') {
    return (content: string) => getTemporaryElementDimensions(content);
  }

  const wrapper = document.createElement('div');
  wrapper.style.position = 'absolute';
  wrapper.style.visibility = 'hidden';
  wrapper.style.top = '-9999px';
  wrapper.style.left = '-9999px';
  wrapper.innerHTML = staticStructureHtml;

  const dynamicElement = wrapper.querySelector('[data-dynamic]') as HTMLElement;
  if (!dynamicElement) {
    throw new Error('Static structure must contain an element with data-dynamic attribute');
  }

  document.body.appendChild(wrapper);

  return (content: string) => {
    dynamicElement.innerHTML = content;
    const rect = wrapper.getBoundingClientRect();
    // Clear so the persistent element doesn't leak measured text into the DOM
    dynamicElement.innerHTML = '';
    return { height: rect.height, width: rect.width };
  };
};

export const disableTransitions = () => {
  if (!globalTransitionNode || typeof document === 'undefined') return;
  document.head.appendChild(globalTransitionNode);
};

export const enableTransitions = () => {
  if (!globalTransitionNode || typeof document === 'undefined') return;

  if (document.head.contains(globalTransitionNode)) {
    document.head.removeChild(globalTransitionNode);
  }
};

export const getFileExtension = (input: string | null | undefined) => {
  if (!input) {
    return '';
  }
  // Try to parse as URL and get pathname
  try {
    input = new URL(input).pathname;
    // Ignore error if it's not a URL
  } catch {}

  const extension = input.split('.').pop();
  return extension && extension !== input ? extension : null;
};

const isMobileUserAgent = () => {
  try {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  } catch (e) {
    return false;
  }
};

export const isMobileDevice = isMobileUserAgent();

const isIOSUserAgent = () => {
  try {
    return /iPad|iPhone|iPod/i.test(navigator.userAgent);
  } catch (e) {
    return false;
  }
};

export const isIOSDevice = isIOSUserAgent();

const getIsIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export const isIframe = getIsIframe();

export const isElementInViewport = (element: HTMLElement) => {
  // Default to true to prevent unnecessary scrolling
  if (!element) return true;

  const rect = element.getBoundingClientRect();

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const fullAppReload = () => {
  window.location.href = '/';
};

export const getBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};
